import React, { Suspense } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// --- Layouts and Core (Load Immediately) ---
import MainLayout from "./Pages/MainLayout.js";
import UserPrivateRoute from "./Components/userPrivateRoutes.js";
import AdminPrivateRoute from "./Admin/Components/adminPrivateRoute.js";
import AdminLayout from "./Admin/Components/adminLayout.js";

// --- Lazy-Loaded Pages ---
// Public Pages
const LandingPage = React.lazy(() => import("./Pages/LandingPage.js"));
const AuthForm = React.lazy(() => import("./Pages/AuthForm.js"));
const HowItWorks = React.lazy(() => import("./Pages/HowItWorks.js"));
const Services = React.lazy(() => import("./Pages/Services.js"));
const NotFoundPage = React.lazy(() => import("./Pages/NotFoundPage.js"));
const VerifyAccount = React.lazy(() => import('./Pages/VerifyAccount.js'));
const ForgotPassword = React.lazy(() => import('./Pages/ForgotPassword.js'));
const ResetPassword = React.lazy(() => import('./Pages/ResetPassword.js'));
const CompleteTaskerProfile = React.lazy(() => import('./Pages/CompleteTaskerProfile.js'));

// User Private Pages
const PostTask = React.lazy(() => import("./Pages/PostTask.js"));
const BrowseTasks = React.lazy(() => import("./Pages/BrowseTasks.js"));
const MyPosts = React.lazy(() => import("./Pages/MyPosts.js"));
const MyTasks = React.lazy(() => import("./Pages/MyTasks.js"));
const Profile = React.lazy(() => import("./Pages/Profile.js"));
const SupportPage = React.lazy(() => import("./Pages/SupportPage.js"));
const ActivityCenter = React.lazy(() => import("./Pages/Activity_Center.js"));
const ChatApp = React.lazy(() => import("./Pages/ChatApp.js"));
const NotificationsPage = React.lazy(() => import("./Pages/Notification.js"));

// Admin Pages
const SetupSuperAdmin = React.lazy(()=> import('./Admin/Pages/setUpSuperAdmin.js'));
const AdminLogin = React.lazy(() => import("./Admin/Pages/adminLogin.js"));
const AdminDashboard = React.lazy(() => import("./Admin/Pages/adminDashboard.js"));
const TaskerManagement = React.lazy(() => import("./Admin/Pages/taskerManagement.js"));
const DisputeManagement = React.lazy(() => import("./Admin/Pages/disputeManagement.js"));
const SupportTickets = React.lazy(() => import("./Admin/Pages/supportTickets.js"));
const UserManagement = React.lazy(() => import("./Admin/Pages/userManagement.js"));
const AdminManagement = React.lazy(() => import("./Admin/Pages/adminManagement.js"));
const CompleteAdminRegistration = React.lazy(() => import("./Admin/Pages/completeAdminRegistration.js"));

// --- Simple Loader Component ---
const PageLoader = () => (
  <div className="flex justify-center items-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
  </div>
);

function App() {
  return (
    <>
      <BrowserRouter>
        {/* Wrap all routes in Suspense to handle lazy-loaded pages */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* --- MAIN LAYOUT (Navbar & Footer) --- */}
            <Route element={<MainLayout />}>
              {/* --- PUBLIC ROUTES --- */}
              <Route path="/" element={<LandingPage />} />
              <Route path="auth" element={<AuthForm />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/services" element={<Services />} />
              <Route path="/verify-email/:token" element={<VerifyAccount />} />
              <Route path="/tasker/verify/:token" element={<VerifyAccount />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:role/:token" element={<ResetPassword />} />

              {/* --- USER PRIVATE ROUTES --- */}
              <Route element={<UserPrivateRoute />}>
                <Route path="/post-task" element={<PostTask />} />
                <Route path="/browse-tasks" element={<BrowseTasks />} />
                <Route path="/my-posts/:taskId?" element={<MyPosts />} />
                <Route path="/my-tasks" element={<MyTasks />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/chat" element={<ChatApp />} />
                <Route path="/activity-center" element={<ActivityCenter />} />
                <Route path="/tasker/complete-profile" element={<CompleteTaskerProfile />} />
              </Route>
            </Route>

            {/* --- ADMIN ROUTES --- */}
            <Route path="/admin/setup" element={<SetupSuperAdmin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminPrivateRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="taskers" element={<TaskerManagement />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="disputes" element={<DisputeManagement />} />
                <Route path="support" element={<SupportTickets />} />
                <Route path="admin-management" element={<AdminManagement />} />
                <Route path="complete-registration" element={<CompleteAdminRegistration />} />
              </Route>
            </Route>

            {/* --- 404 PAGE --- */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;
